import * as THREE from "three";
import type { MapData } from "@dogdell/shared";
import { InstancedVegetation, vegScale } from "./InstancedVegetation";
import { natureTemplates } from "./NatureAssets";
import { cloneCar } from "./CarAssets";
import { cloneRoad } from "./RoadAssets";
import { sharedSphere, WORLD_PROP_SCALE } from "./WorldModels";

/** How far past the park rim roads / grass extend (hides sky seam inside fog). */
const HORIZON_REACH = 110;
const SKIRT_PAD = 130;

interface HorizonCar {
  mesh: THREE.Group;
  axis: "x" | "z";
  fixed: number;
  min: number;
  max: number;
  speed: number;
  dir: number;
  t: number;
}

/**
 * Unreachable scenery beyond the walkable park: continuous forest, city roads,
 * parking lots with cars - keeps asphalt and traffic outside the playable rim.
 */
export class HorizonDecor {
  readonly root = new THREE.Group();
  private readonly cars: HorizonCar[] = [];
  private vegetation: InstancedVegetation | null = null;

  constructor(private readonly map: MapData) {
    this.buildSkirt();
    this.buildRoadsAndParking();
    this.buildForest();
  }

  update(dtSec: number): void {
    for (const car of this.cars) {
      car.t += car.speed * car.dir * dtSec;
      const span = car.max - car.min;
      if (span <= 0) {
        continue;
      }
      const u = ((car.t - car.min) % span + span) % span;
      const pos = car.min + u;
      if (car.axis === "x") {
        car.mesh.position.x = pos;
        car.mesh.position.z = car.fixed;
        // Nose is +X after CarAssets bake; aim into travel direction.
        car.mesh.rotation.y = car.dir > 0 ? 0 : Math.PI;
      } else {
        car.mesh.position.x = car.fixed;
        car.mesh.position.z = pos;
        car.mesh.rotation.y = car.dir > 0 ? -Math.PI / 2 : Math.PI / 2;
      }
    }
  }

  destroy(): void {
    this.vegetation?.destroy();
    this.vegetation = null;
    this.root.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (!child.geometry.userData.shared) {
          child.geometry.dispose();
        }
        if (child.material instanceof THREE.Material && !child.material.userData.shared) {
          child.material.dispose();
        }
      }
    });
    this.root.removeFromParent();
    this.cars.length = 0;
  }

  private buildSkirt(): void {
    const pad = SKIRT_PAD;
    const w = this.map.width + pad * 2;
    const h = this.map.height + pad * 2;
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshLambertMaterial({ color: 0xa0cc68 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(this.map.width / 2, -0.35, this.map.height / 2);
    this.root.add(ground);

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(
        Math.max(this.map.width, this.map.height) * 0.55,
        Math.max(this.map.width, this.map.height) * 0.95 + HORIZON_REACH * 0.55,
        48,
      ),
      new THREE.MeshLambertMaterial({ color: 0x86c058, side: THREE.DoubleSide }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(this.map.width / 2, -0.32, this.map.height / 2);
    this.root.add(ring);
  }

  private buildForest(): void {
    const w = this.map.width;
    const h = this.map.height;
    const batch = new InstancedVegetation();
    this.vegetation = batch;

    const plantAt = (x: number, z: number, scaleBoost: number) => {
      if (x >= -0.5 && x < w + 0.5 && z >= -0.5 && z < h + 0.5) {
        return;
      }
      if (isHardscape(x, z, w, h)) {
        return;
      }
      // Keep wide meadows beside belt roads so asphalt reads as a clearing.
      if (nearRoadVista(x, z, w, h)) {
        return;
      }
      const n = hash2(Math.floor(x * 10), Math.floor(z * 10));
      const wx = x + (n - 0.5) * 0.9;
      const wz = z + (hash2(Math.floor(z * 10), Math.floor(x * 10)) - 0.5) * 0.9;
      // Fewer near-rim bushes - they read as clutter and still cost instances.
      if (n < 0.12) {
        const bushCount = Math.max(1, natureTemplates("bush").length);
        batch.addBush({
          x: wx,
          y: -0.1,
          z: wz,
          rotY: n * Math.PI * 2,
          scale: vegScale(2.05 + n * 0.7 + scaleBoost * 0.18, 0),
          variant: Math.floor(n * bushCount * 7) % bushCount,
        });
        return;
      }
      const pineCount = natureTemplates("pine").length;
      const treeCount = Math.max(1, natureTemplates("tree").length);
      const usePine = pineCount > 0 && n > 0.55;
      batch.addTree({
        x: wx,
        y: -0.12,
        z: wz,
        rotY: n * Math.PI * 2,
        // Fewer trees, larger canopy - same wall, lower instance/tri count.
        scale: (5.4 + n * 2.1 + scaleBoost * 1.8) * WORLD_PROP_SCALE,
        variant: usePine
          ? Math.floor(n * pineCount * 5) % pineCount
          : Math.floor(n * treeCount * 5) % treeCount,
        family: usePine ? "pine" : "tree",
        autumn: !usePine && hash2(Math.floor(wx), Math.floor(wz)) < 0.38,
      });
    };

    // Near rim - sparse chunks, but leave road approaches open.
    for (let z = -10; z < h + 10; z += 6.2) {
      for (let x = -10; x < w + 10; x += 6.2) {
        const dist = edgeDist(x, z, w, h);
        if (dist < 0.5 || dist > 9) {
          continue;
        }
        if (hash2(Math.floor(x * 3), Math.floor(z * 3)) > 0.08) {
          continue;
        }
        plantAt(x, z, dist * 0.03);
      }
    }

    // Mid belt - fills gaps without stacking a second dense wall.
    for (let z = -22; z < h + 22; z += 9.4) {
      for (let x = -22; x < w + 22; x += 9.4) {
        const dist = edgeDist(x, z, w, h);
        if (dist < 8 || dist > 20) {
          continue;
        }
        if (hash2(Math.floor(x * 2), Math.floor(z * 2)) > 0.11) {
          continue;
        }
        plantAt(x, z, 0.4 + dist * 0.02);
      }
    }

    // Far canopy - sparse hero trees; soft blobs (below) sell density past fog.
    for (let z = -HORIZON_REACH; z < h + HORIZON_REACH; z += 14) {
      for (let x = -HORIZON_REACH; x < w + HORIZON_REACH; x += 14) {
        const dist = edgeDist(x, z, w, h);
        if (dist < 22 || dist > HORIZON_REACH * 0.88) {
          continue;
        }
        if (hash2(Math.floor(x), Math.floor(z)) > 0.16) {
          continue;
        }
        plantAt(x, z, 0.9 + dist * 0.012);
      }
    }

    // Cheap distant “canopy blobs” - one draw, hides sky seams without Kenney mesh cost.
    const blobGeo = sharedSphere(1, 6, 4);
    const blobMat = new THREE.MeshLambertMaterial({ color: 0x7cbc5c });
    const blobCount = 36;
    const blobs = new THREE.InstancedMesh(blobGeo, blobMat, blobCount);
    const dummy = new THREE.Object3D();
    for (let i = 0; i < blobCount; i++) {
      const t = i / blobCount;
      const angle = t * Math.PI * 2;
      const radius = Math.max(w, h) * 0.72 + 36 + hash2(i, 9) * 42;
      dummy.position.set(
        w / 2 + Math.cos(angle) * radius,
        4.2 + hash2(i, 7) * 4.2,
        h / 2 + Math.sin(angle) * radius,
      );
      const s = 7.5 + hash2(i, 2) * 7.5;
      dummy.scale.set(s * 1.7, s * 1.1, s * 1.7);
      dummy.updateMatrix();
      blobs.setMatrixAt(i, dummy.matrix);
    }
    blobs.instanceMatrix.needsUpdate = true;
    this.root.add(blobs);

    batch.build();
    this.root.add(batch.root);
  }

  private buildRoadsAndParking(): void {
    const w = this.map.width;
    const h = this.map.height;
    const asphalt = new THREE.MeshLambertMaterial({ color: 0x7a736c });
    const curb = new THREE.MeshLambertMaterial({ color: 0xc4b8a8 });
    const lineMat = new THREE.MeshBasicMaterial({ color: 0xffe082 });
    const stallMat = new THREE.MeshBasicMaterial({ color: 0xf0ebe0 });
    const reach = HORIZON_REACH;
    const beltLen = Math.max(w, h) + reach * 2;

    const addEW = (z: number, length: number, cx = w / 2, thickness = 5.4) => {
      const road = new THREE.Mesh(new THREE.BoxGeometry(length, 0.06, thickness), asphalt);
      road.position.set(cx, -0.28, z);
      this.root.add(road);
      const line = new THREE.Mesh(new THREE.BoxGeometry(length * 0.94, 0.02, 0.16), lineMat);
      line.position.set(cx, -0.22, z);
      this.root.add(line);
    };
    const addNS = (x: number, length: number, cz = h / 2, thickness = 5.4) => {
      const road = new THREE.Mesh(new THREE.BoxGeometry(thickness, 0.06, length), asphalt);
      road.position.set(x, -0.28, cz);
      this.root.add(road);
      const line = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.02, length * 0.94), lineMat);
      line.position.set(x, -0.22, cz);
      this.root.add(line);
    };

    // Inner belt around the park.
    const roadN = -11;
    const roadS = h + 11;
    const roadW = -11;
    const roadE = w + 11;
    addEW(roadN, beltLen);
    addEW(roadS, beltLen);
    addNS(roadW, beltLen);
    addNS(roadE, beltLen);

    // Outer belt - pushes asphalt past fog so edges don't dump into sky.
    const roadN2 = -36;
    const roadS2 = h + 36;
    const roadW2 = -36;
    const roadE2 = w + 36;
    addEW(roadN2, beltLen + 40);
    addEW(roadS2, beltLen + 40);
    addNS(roadW2, beltLen + 40);
    addNS(roadE2, beltLen + 40);

    // Long outbound spurs from mid-sides (roads continuing into the distance).
    addEW(roadN, reach * 0.85, -reach * 0.35);
    addEW(roadN, reach * 0.85, w + reach * 0.35);
    addEW(roadS, reach * 0.85, -reach * 0.35);
    addEW(roadS, reach * 0.85, w + reach * 0.35);
    addNS(roadE, reach * 0.85, -reach * 0.35);
    addNS(roadE, reach * 0.85, h + reach * 0.35);
    addNS(roadW, reach * 0.85, -reach * 0.35);
    addNS(roadW, reach * 0.85, h + reach * 0.35);

    // Kenney crossroad accents at inner-belt corners.
    this.placeCrossroad(roadW, roadN, 0);
    this.placeCrossroad(roadE, roadN, Math.PI / 2);
    this.placeCrossroad(roadW, roadS, -Math.PI / 2);
    this.placeCrossroad(roadE, roadS, Math.PI);
    this.placeCrossroad(roadW2, roadN2, 0);
    this.placeCrossroad(roadE2, roadS2, Math.PI);
    // Street lamps along the east belt (reads from plaza gate).
    for (const z of [h * 0.25, h * 0.5, h * 0.75]) {
      this.placeStreetLight(roadE + 3.2, z);
      this.placeStreetLight(roadW - 3.2, z);
    }

    // Driveway stubs from belt road toward park gates (still outside walkable map).
    const driveway = (cx: number, cz: number, dx: number, dz: number, len: number) => {
      const alongX = Math.abs(dx) >= Math.abs(dz);
      const geo = alongX
        ? new THREE.BoxGeometry(len, 0.05, 3.2)
        : new THREE.BoxGeometry(3.2, 0.05, len);
      const mesh = new THREE.Mesh(geo, asphalt);
      mesh.position.set(cx + dx * (len * 0.5), -0.27, cz + dz * (len * 0.5));
      this.root.add(mesh);
    };
    driveway(w + 0.5, 28.5, 1, 0, 10); // east entrance → plaza
    driveway(w / 2, -0.5, 0, -1, 10); // north curb cut
    driveway(-0.5, 56, -1, 0, 10); // west athletic fields

    // Parking lots sit outside the road belt (road half-width ~2.7) so stalls aren't on traffic.
    const roadHalf = 2.9;
    this.addParkingLot({
      cx: roadE + roadHalf + 7,
      cz: h * 0.28,
      sx: 14,
      sz: 26,
      stallsAlong: "z",
      asphalt,
      curb,
      stallMat,
    });
    this.addParkingLot({
      cx: w * 0.28,
      cz: roadN - roadHalf - 6,
      sx: 32,
      sz: 12,
      stallsAlong: "x",
      asphalt,
      curb,
      stallMat,
    });
    this.addParkingLot({
      cx: roadW - roadHalf - 6,
      cz: h * 0.45,
      sx: 12,
      sz: 28,
      stallsAlong: "z",
      asphalt,
      curb,
      stallMat,
    });
    this.addParkingLot({
      cx: w * 0.55,
      cz: roadS + roadHalf + 6,
      sx: 30,
      sz: 12,
      stallsAlong: "x",
      asphalt,
      curb,
      stallMat,
    });
    // Far lots so the outer belt still has parked cars past the first ring.
    this.addParkingLot({
      cx: roadE2 + roadHalf + 8,
      cz: h * 0.55,
      sx: 16,
      sz: 22,
      stallsAlong: "z",
      asphalt,
      curb,
      stallMat,
    });
    this.addParkingLot({
      cx: w * 0.4,
      cz: roadN2 - roadHalf - 7,
      sx: 26,
      sz: 14,
      stallsAlong: "x",
      asphalt,
      curb,
      stallMat,
    });

    const colors = [0xff5252, 0x40c4ff, 0xffee58, 0xea80fc, 0x69f0ae, 0xffab40, 0xf48fb1, 0xffffff, 0x90caf9, 0x78909c, 0xff7043, 0x26a69a];
    const laneReach = reach + 8;
    const laneHalf = 1.15;
    // Right-hand traffic: keep to the right relative to travel direction.
    // N/S roads run along X; E/W roads run along Z.
    const lanes: { axis: "x" | "z"; fixed: number; min: number; max: number; dir: number }[] = [
      // North belt: eastbound on south (park) side, westbound on north side.
      { axis: "x", fixed: roadN + laneHalf, min: -laneReach, max: w + laneReach, dir: 1 },
      { axis: "x", fixed: roadN - laneHalf, min: -laneReach, max: w + laneReach, dir: -1 },
      // South belt: eastbound on south side, westbound on north (park) side.
      { axis: "x", fixed: roadS + laneHalf, min: -laneReach, max: w + laneReach, dir: 1 },
      { axis: "x", fixed: roadS - laneHalf, min: -laneReach, max: w + laneReach, dir: -1 },
      // West belt: southbound on west side, northbound on east (park) side.
      { axis: "z", fixed: roadW - laneHalf, min: -laneReach, max: h + laneReach, dir: 1 },
      { axis: "z", fixed: roadW + laneHalf, min: -laneReach, max: h + laneReach, dir: -1 },
      // East belt: southbound on west (park) side, northbound on east side.
      { axis: "z", fixed: roadE - laneHalf, min: -laneReach, max: h + laneReach, dir: 1 },
      { axis: "z", fixed: roadE + laneHalf, min: -laneReach, max: h + laneReach, dir: -1 },
      // Outer belt (same RHT rules, slightly longer reach).
      { axis: "x", fixed: roadN2 + laneHalf, min: -laneReach - 20, max: w + laneReach + 20, dir: 1 },
      { axis: "x", fixed: roadN2 - laneHalf, min: -laneReach - 20, max: w + laneReach + 20, dir: -1 },
      { axis: "x", fixed: roadS2 + laneHalf, min: -laneReach - 20, max: w + laneReach + 20, dir: 1 },
      { axis: "x", fixed: roadS2 - laneHalf, min: -laneReach - 20, max: w + laneReach + 20, dir: -1 },
      { axis: "z", fixed: roadW2 - laneHalf, min: -laneReach - 20, max: h + laneReach + 20, dir: 1 },
      { axis: "z", fixed: roadW2 + laneHalf, min: -laneReach - 20, max: h + laneReach + 20, dir: -1 },
      { axis: "z", fixed: roadE2 - laneHalf, min: -laneReach - 20, max: h + laneReach + 20, dir: 1 },
      { axis: "z", fixed: roadE2 + laneHalf, min: -laneReach - 20, max: h + laneReach + 20, dir: -1 },
    ];

    const carsPerLane = 3;
    let carI = 0;
    for (let li = 0; li < lanes.length; li++) {
      const lane = lanes[li]!;
      const count = li >= 8 ? 2 : carsPerLane;
      for (let c = 0; c < count; c++) {
        const mesh = makeVehicle(colors[carI % colors.length]!, carI);
        const t0 =
          lane.min +
          ((c + 0.2 + hash2(carI, 3) * 0.55 + (lane.dir < 0 ? 0.35 : 0)) / count) *
            (lane.max - lane.min);
        if (lane.axis === "x") {
          mesh.position.set(t0, -0.06, lane.fixed);
        } else {
          mesh.position.set(lane.fixed, -0.06, t0);
        }
        this.root.add(mesh);
        const kind = vehicleKindFromSeed(carI);
        const baseSpeed =
          kind === "boxTruck" || kind === "van" ? 3.6
          : kind === "pickup" || kind === "suv" ? 4.6
          : 5.8;
        this.cars.push({
          mesh,
          axis: lane.axis,
          fixed: lane.fixed,
          min: lane.min,
          max: lane.max,
          speed: baseSpeed + hash2(carI, 8) * 1.6,
          dir: lane.dir,
          t: t0,
        });
        carI++;
      }
    }
  }

  private placeCrossroad(x: number, z: number, rotY: number): void {
    const piece =
      cloneRoad("road-crossroad") ??
      cloneRoad("road-intersection") ??
      cloneRoad("road-straight");
    if (!piece) {
      return;
    }
    piece.position.set(x, -0.22, z);
    piece.rotation.y = rotY;
    this.root.add(piece);
  }

  private placeStreetLight(x: number, z: number): void {
    const light = cloneRoad("light-curved") ?? cloneRoad("light-square");
    if (!light) {
      return;
    }
    light.position.set(x, -0.2, z);
    light.rotation.y = hash2(Math.floor(x), Math.floor(z)) * Math.PI * 2;
    this.root.add(light);
  }

  private addParkingLot(opts: {
    cx: number;
    cz: number;
    sx: number;
    sz: number;
    stallsAlong: "x" | "z";
    asphalt: THREE.Material;
    curb: THREE.Material;
    stallMat: THREE.Material;
  }): void {
    const { cx, cz, sx, sz, stallsAlong, asphalt, curb, stallMat } = opts;
    const pad = new THREE.Mesh(new THREE.BoxGeometry(sx, 0.05, sz), asphalt);
    pad.position.set(cx, -0.26, cz);
    this.root.add(pad);

    const rim = new THREE.Mesh(new THREE.BoxGeometry(sx + 0.6, 0.08, sz + 0.6), curb);
    rim.position.set(cx, -0.3, cz);
    this.root.add(rim);

    // Head-in stalls: aisle down the middle, cars pulled into bays (not parallel in the aisle).
    // Kenney clones nose toward +X at identity. Depth/pitch match ~3.2u car length.
    const stallPitch = 3.45;
    const stallDepth = 3.55;
    const aisle = Math.min(4.0, Math.max(3.0, (stallsAlong === "z" ? sx : sz) - stallDepth * 2 - 0.6));
    const bayCenter = aisle * 0.5 + stallDepth * 0.5;

    if (stallsAlong === "z") {
      const rows = Math.max(2, Math.floor((sz - 2.2) / stallPitch));
      const z0 = cz - ((rows - 1) * stallPitch) * 0.5;
      for (let r = 0; r < rows; r++) {
        const lz = z0 + r * stallPitch;
        for (const side of [-1, 1] as const) {
          const lx = cx + side * bayCenter;
          // Stall dividers (short lines across the bay mouth).
          const stripe = new THREE.Mesh(new THREE.BoxGeometry(stallDepth, 0.02, 0.07), stallMat);
          stripe.position.set(lx, -0.21, lz - stallPitch * 0.45);
          this.root.add(stripe);
          if (r === rows - 1) {
            const end = new THREE.Mesh(new THREE.BoxGeometry(stallDepth, 0.02, 0.07), stallMat);
            end.position.set(lx, -0.21, lz + stallPitch * 0.45);
            this.root.add(end);
          }
          if (hash2(r + 3, side + 11) > 0.28) {
            const car = makeVehicle(
              PARKED_COLORS[(r * 3 + (side + 1)) % PARKED_COLORS.length]!,
              r * 10 + side + 2,
              true,
            );
            car.position.set(lx, -0.12, lz);
            // Left bank faces -X (into curb); right bank faces +X.
            car.rotation.y = side < 0 ? Math.PI : 0;
            this.root.add(car);
          }
        }
      }
    } else {
      const cols = Math.max(2, Math.floor((sx - 2.2) / stallPitch));
      const x0 = cx - ((cols - 1) * stallPitch) * 0.5;
      for (let c = 0; c < cols; c++) {
        const lx = x0 + c * stallPitch;
        for (const side of [-1, 1] as const) {
          const lz = cz + side * bayCenter;
          const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.02, stallDepth), stallMat);
          stripe.position.set(lx - stallPitch * 0.45, -0.21, lz);
          this.root.add(stripe);
          if (c === cols - 1) {
            const end = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.02, stallDepth), stallMat);
            end.position.set(lx + stallPitch * 0.45, -0.21, lz);
            this.root.add(end);
          }
          if (hash2(c + 5, side + 17) > 0.3) {
            const car = makeVehicle(
              PARKED_COLORS[(c * 2 + (side + 1)) % PARKED_COLORS.length]!,
              c * 10 + side + 2,
              true,
            );
            car.position.set(lx, -0.12, lz);
            // North bank faces -Z; south bank faces +Z.
            car.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
            this.root.add(car);
          }
        }
      }
    }
  }
}

const PARKED_COLORS = [0xff8a80, 0x80d8ff, 0xffe082, 0xce93d8, 0xa5d6a7, 0xffcc80, 0xb0bec5, 0xf8bbd0];

type VehicleKind = "sedan" | "hatchback" | "suv" | "pickup" | "van" | "boxTruck";

function vehicleKindFromSeed(seed: number): VehicleKind {
  // Bias toward cars; trucks appear less often.
  const roll = hash2(seed, 19);
  if (roll < 0.22) {
    return "sedan";
  }
  if (roll < 0.4) {
    return "hatchback";
  }
  if (roll < 0.58) {
    return "suv";
  }
  if (roll < 0.74) {
    return "pickup";
  }
  if (roll < 0.88) {
    return "van";
  }
  return "boxTruck";
}

function addWheels(
  group: THREE.Group,
  wheelMat: THREE.Material,
  positions: readonly (readonly [number, number])[],
  radius = 0.16,
  width = 0.14,
): void {
  const wheelGeo = new THREE.CylinderGeometry(radius, radius, width, 6);
  wheelGeo.rotateZ(Math.PI / 2);
  for (const [x, z] of positions) {
    const wheel = new THREE.Mesh(wheelGeo, wheelMat);
    wheel.position.set(x, radius, z);
    group.add(wheel);
  }
}

function addLamps(group: THREE.Group, length: number, height: number, width: number, parked: boolean): void {
  if (parked) {
    return;
  }
  const lampMat = new THREE.MeshBasicMaterial({ color: 0xfff59d });
  const tailMat = new THREE.MeshBasicMaterial({ color: 0xff5252 });
  const lampGeo = new THREE.BoxGeometry(0.12, 0.1, 0.16);
  const halfL = length * 0.5 - 0.04;
  const halfW = width * 0.32;
  for (const z of [halfW, -halfW] as const) {
    const lamp = new THREE.Mesh(lampGeo, lampMat);
    lamp.position.set(halfL, height, z);
    const tail = new THREE.Mesh(lampGeo, tailMat);
    tail.position.set(-halfL, height, z);
    group.add(lamp, tail);
  }
}

/** Prefer Kenney Car Kit; fall back to procedural toy silhouettes. */
function makeVehicle(color: number, seed = 0, parked = false): THREE.Group {
  const kenney = cloneCar(seed);
  if (kenney) {
    const id = String(kenney.userData.carId ?? "");
    const truckish = /truck|van|delivery|ambulance|garbage|fire/.test(id);
    // Full road-relative size (belt is ~5.4 wide). Not miniature “far away” props —
    // the belt sits at the park rim in the same world units as trees / dogs.
    const scale =
      parked
        ? truckish ? 1.58 : 1.72
        : truckish ? 1.68 : 1.82;
    kenney.scale.setScalar(scale);
    return kenney;
  }
  return makeVehicleProcedural(color, seed, parked);
}

/** Distinct toy vehicle silhouettes - shape changes, not just paint. */
function makeVehicleProcedural(color: number, seed = 0, parked = false): THREE.Group {
  const kind = vehicleKindFromSeed(seed);
  const group = new THREE.Group();
  const bodyMat = new THREE.MeshLambertMaterial({ color });
  const glassMat = new THREE.MeshLambertMaterial({ color: 0xe8f4ff });
  const darkMat = new THREE.MeshLambertMaterial({ color: 0x37474f });
  const wheelMat = new THREE.MeshLambertMaterial({ color: 0x263238 });
  const accent = new THREE.MeshLambertMaterial({
    color: kind === "boxTruck" || kind === "van" ? 0xcfd8dc : color,
  });

  if (kind === "sedan") {
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.42, 0.78), bodyMat);
    body.position.y = 0.36;
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.38, 0.7), glassMat);
    cabin.position.set(-0.05, 0.68, 0);
    const trunk = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.22, 0.72), bodyMat);
    trunk.position.set(-0.68, 0.48, 0);
    group.add(body, cabin, trunk);
    addWheels(group, wheelMat, [
      [-0.52, 0.36],
      [0.52, 0.36],
      [-0.52, -0.36],
      [0.52, -0.36],
    ]);
    addLamps(group, 1.7, 0.34, 0.78, parked);
  } else if (kind === "hatchback") {
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.48, 0.82), bodyMat);
    body.position.y = 0.38;
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.46, 0.76), glassMat);
    cabin.position.set(0.05, 0.74, 0);
    group.add(body, cabin);
    addWheels(group, wheelMat, [
      [-0.42, 0.38],
      [0.42, 0.38],
      [-0.42, -0.38],
      [0.42, -0.38],
    ], 0.15);
    addLamps(group, 1.45, 0.36, 0.82, parked);
  } else if (kind === "suv") {
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.75, 0.55, 0.92), bodyMat);
    body.position.y = 0.42;
    const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.55, 0.86), glassMat);
    cabin.position.set(-0.05, 0.82, 0);
    const rack = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.06, 0.7), darkMat);
    rack.position.set(-0.05, 1.12, 0);
    group.add(body, cabin, rack);
    addWheels(group, wheelMat, [
      [-0.55, 0.42],
      [0.55, 0.42],
      [-0.55, -0.42],
      [0.55, -0.42],
    ], 0.19, 0.16);
    addLamps(group, 1.75, 0.4, 0.92, parked);
  } else if (kind === "pickup") {
    const cab = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.55, 0.86), bodyMat);
    cab.position.set(0.35, 0.48, 0);
    const glass = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.38, 0.8), glassMat);
    glass.position.set(0.32, 0.85, 0);
    const bed = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.32, 0.82), darkMat);
    bed.position.set(-0.5, 0.4, 0);
    const railL = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.22, 0.06), bodyMat);
    railL.position.set(-0.5, 0.58, 0.38);
    const railR = railL.clone();
    railR.position.z = -0.38;
    group.add(cab, glass, bed, railL, railR);
    addWheels(group, wheelMat, [
      [-0.55, 0.4],
      [0.5, 0.4],
      [-0.55, -0.4],
      [0.5, -0.4],
    ], 0.18, 0.15);
    addLamps(group, 1.8, 0.38, 0.86, parked);
  } else if (kind === "van") {
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.85, 0.95, 0.9), bodyMat);
    body.position.y = 0.62;
    const nose = new THREE.Mesh(new THREE.BoxGeometry(0.35, 0.5, 0.86), accent);
    nose.position.set(0.85, 0.42, 0);
    const glass = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.42, 0.78), glassMat);
    glass.position.set(0.95, 0.78, 0);
    group.add(body, nose, glass);
    addWheels(group, wheelMat, [
      [-0.55, 0.4],
      [0.55, 0.4],
      [-0.55, -0.4],
      [0.55, -0.4],
    ], 0.17);
    addLamps(group, 1.95, 0.38, 0.9, parked);
  } else {
    // Box truck / delivery
    const cab = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.7, 0.9), bodyMat);
    cab.position.set(0.75, 0.55, 0);
    const glass = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.4, 0.82), glassMat);
    glass.position.set(1.08, 0.78, 0);
    const box = new THREE.Mesh(new THREE.BoxGeometry(1.55, 1.15, 0.98), accent);
    box.position.set(-0.35, 0.82, 0);
    const bumper = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.18, 0.92), darkMat);
    bumper.position.set(1.15, 0.28, 0);
    group.add(cab, glass, box, bumper);
    addWheels(group, wheelMat, [
      [-0.7, 0.42],
      [0.75, 0.42],
      [-0.7, -0.42],
      [0.75, -0.42],
    ], 0.2, 0.17);
    addLamps(group, 2.15, 0.36, 0.9, parked);
  }

  // Same road-relative scale for traffic and parking.
  const scale =
    parked
      ? kind === "boxTruck" || kind === "van" ? 1.55 : 1.7
      : kind === "boxTruck" || kind === "van" ? 1.65 : 1.8;
  group.scale.setScalar(scale);
  return group;
}

function edgeDist(x: number, z: number, w: number, h: number): number {
  const dx = x < 0 ? -x : x > w ? x - w : 0;
  const dz = z < 0 ? -z : z > h ? z - h : 0;
  if (dx === 0 && dz === 0) {
    return Math.min(x, w - x, z, h - z);
  }
  return Math.hypot(dx, dz);
}

/** Roads + parking lots - keep trees clear of asphalt (wide enough to see traffic). */
function isHardscape(x: number, z: number, w: number, h: number): boolean {
  const roadN = -11;
  const roadS = h + 11;
  const roadW = -11;
  const roadE = w + 11;
  const roadN2 = -36;
  const roadS2 = h + 36;
  const roadW2 = -36;
  const roadE2 = w + 36;
  // Wide clear belts so cars aren't buried in the tree line.
  if (Math.abs(z - roadN) < 9.5 || Math.abs(z - roadS) < 9.5) {
    return true;
  }
  if (Math.abs(x - roadW) < 9.5 || Math.abs(x - roadE) < 9.5) {
    return true;
  }
  if (Math.abs(z - roadN2) < 9 || Math.abs(z - roadS2) < 9) {
    return true;
  }
  if (Math.abs(x - roadW2) < 9 || Math.abs(x - roadE2) < 9) {
    return true;
  }
  // East lot (plaza entrance)
  if (x > w + 1 && x < w + 14 && z > 16 && z < 40) {
    return true;
  }
  // Far east lot
  if (x > w + 14 && x < w + 30 && z > h * 0.4 && z < h * 0.7) {
    return true;
  }
  // North lot
  if (z < -2 && z > -13 && x > w * 0.12 && x < w * 0.45) {
    return true;
  }
  // Far north lot
  if (z < -14 && z > -29 && x > w * 0.25 && x < w * 0.55) {
    return true;
  }
  // West lot
  if (x < -2 && x > -13 && z > h * 0.3 && z < h * 0.62) {
    return true;
  }
  // South lot
  if (z > h + 2 && z < h + 13 && x > w * 0.4 && x < w * 0.72) {
    return true;
  }
  // Driveways
  if (x > w && x < w + 11 && z > 26 && z < 31) {
    return true;
  }
  if (z < 0 && z > -11 && x > w / 2 - 2 && x < w / 2 + 2) {
    return true;
  }
  if (x < 0 && x > -11 && z > 54 && z < 58) {
    return true;
  }
  return false;
}

/**
 * Extra meadow beside belt roads (especially the extended EW strips past the
 * park corners) so left/right tree walls don't hide the asphalt from the rim.
 */
function nearRoadVista(x: number, z: number, w: number, h: number): boolean {
  const roadN = -11;
  const roadS = h + 11;
  const roadW = -11;
  const roadE = w + 11;
  const roadN2 = -36;
  const roadS2 = h + 36;
  // Soft shoulder past the hardscape belt - still mostly clear, a few trees ok.
  const shoulder = (along: number, center: number) => {
    const d = Math.abs(along - center);
    if (d < 14) {
      return true;
    }
    if (d < 22) {
      return hash2(Math.floor(x * 2), Math.floor(z * 2)) > 0.22;
    }
    return false;
  };
  if (shoulder(z, roadN) || shoulder(z, roadS) || shoulder(z, roadN2) || shoulder(z, roadS2)) {
    return true;
  }
  if (shoulder(x, roadW) || shoulder(x, roadE)) {
    return true;
  }
  return false;
}

function hash2(a: number, b: number): number {
  const n = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return n - Math.floor(n);
}
